<script setup lang="ts">
  import { onMounted, computed, provide } from 'vue';
  import { NoDocuments, PaginateOrScroll } from '@/shared/ui';
  import { DocumentListHeader, DocumentManagementFilterModal } from '@/features/Document';
  import { SortOrderInjectionKey, RefreshDocumentListKey } from '@/shared/lib/sortOrder';
  import DocumentListTable from './DocumentListTable.vue';
  import { useTableFilterModal } from '@/shared/lib/use';
  import { useFilterStore } from '@/entities/Filter';
  import useDocumentListSearch, { documentListSearchProps } from '../model/useDocumentListSearch';
  import { useDocumentManagementExport } from '../model/useDocumentManagementExport';
  import NoFilesIcon from '@/shared/assets/images/no-documents.svg';
  import type { DocumentRevision } from '@/entities/Document';
  import type { SearchFilter } from '@/shared/lib';

  const props = defineProps(documentListSearchProps);

  const emit = defineEmits<{
    'open-external-upload': [];
    'replace-document': [revision: DocumentRevision];
  }>();

  const {
    revisions,
    metadata,
    allRevisions,
    loadingMore,
    hasMoreRevisions,
    selectedQuantity,
    shouldShowTable,
    shouldShowQuantitySelect,
    shouldShowEmptyPage,
    handleSearch,
    handleSortOptions,
    handleFilter,
    onPageChanged,
    handleQuantityChange,
    initSearch,
    removeRevisions,
    sort,
    order,
    filter,
    searchTerm,
  } = useDocumentListSearch(props, 'gestao');

  const { handleExportToExcel, isExporting, isExportDisabled } = useDocumentManagementExport({
    searchTerm,
    sort,
    order,
    filter,
    metadata,
  });

  provide(SortOrderInjectionKey, {
    sort,
    order,
  });

  provide(RefreshDocumentListKey, () => initSearch());

  const filterStore = useFilterStore();
  const { isFilterActive, openFilterModal } = useTableFilterModal(
    DocumentManagementFilterModal,
    filterStore,
    handleFilter,
    () => filter.value
  );

  const handleFilterClicked = () => {
    openFilterModal();
  };

  const hasDocuments = computed(() => {
    return revisions.value && revisions.value.length > 0;
  });

  onMounted(async () => {
    await initSearch();
  });
</script>

<template>
  <div class="w-full">
    <DocumentListHeader
      :has-documents="hasDocuments"
      :selected-quantity="selectedQuantity"
      :should-show-quantity-select="shouldShowQuantitySelect"
      :export-disabled="isExportDisabled"
      :export-loading="isExporting"
      @search="handleSearch"
      @open-external-upload="emit('open-external-upload')"
      @quantity-change="handleQuantityChange"
      @export-to-excel="handleExportToExcel"
    />

    <PaginateOrScroll
      v-if="shouldShowTable"
      :items="revisions"
      :all-items="allRevisions"
      :has-more-items="hasMoreRevisions"
      :loading-more="loadingMore"
      :metadata="metadata"
      :selected-quantity="selectedQuantity"
      @page-changed="onPageChanged"
    >
      <template #table="{ items }">
        <DocumentListTable
          :revisions="items as DocumentRevision[]"
          :is-filter-active="isFilterActive"
          @update:sort="handleSortOptions"
          @update:filter="(f) => handleFilter(f as SearchFilter)"
          @filter-clicked="handleFilterClicked"
          @delete="removeRevisions"
          @replace="emit('replace-document', $event)"
        />
      </template>
    </PaginateOrScroll>
    <NoDocuments
      v-if="shouldShowEmptyPage"
      title="Ops! Parece que ainda não há documentos no GD"
      :icon="NoFilesIcon"
    />
  </div>
</template>
